import { useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/Seo";
import { PageHero, CtaBand } from "@/components/Blocks";
import { PORTFOLIO, PORTFOLIO_CATEGORIES } from "@/data/portfolio";

const LINK_LABEL = {
  "Websites": "Visit website",
  "Mobile apps": "View on Google Play",
  "Browser extensions": "View on Chrome Web Store",
};

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

export default function Portfolio() {
  const [category, setCategory] = useState("All");
  const crumbs = [["Home", "/"], ["Portfolio", "/portfolio"]];
  const items = useMemo(
    () => (category === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === category)),
    [category],
  );
  const countFor = (c) => (c === "All" ? PORTFOLIO.length : PORTFOLIO.filter((p) => p.category === c).length);

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Industrix Solution portfolio",
    numberOfItems: PORTFOLIO.length,
    itemListElement: PORTFOLIO.map((p, i) => ({ "@type": "ListItem", position: i + 1, name: p.name, url: p.url })),
  };

  return (
    <>
      <Seo
        title="Portfolio | Websites, Apps & Software by Industrix Solution"
        description="Explore websites, mobile apps and browser extensions built by Industrix Solution for businesses across India and overseas."
        path="/portfolio"
        jsonLd={[breadcrumbSchema(crumbs), itemListSchema]}
      />
      <PageHero
        crumbs={crumbs}
        eyebrow="Our portfolio"
        lines={["Websites, Apps", "& Software", "We Have Built"]}
        intro="A selection of business websites, web applications, mobile apps and browser extensions delivered for clients across manufacturing, services, retail, healthcare, education and more."
      />

      <section className="wrap py-16 lg:py-24" data-testid="portfolio-list">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter portfolio by type">
          {PORTFOLIO_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={category === c}
              onClick={() => setCategory(c)}
              className={`inline-flex min-h-[44px] items-center gap-2 rounded-full border px-5 text-[14px] font-semibold transition-colors ${category === c ? "border-charcoal bg-charcoal text-ivory" : "border-[#CFCABE] bg-white text-charcoal hover:border-charcoal"}`}
              data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
              <span className={`font-mono text-[12px] ${category === c ? "text-[#E8B795]" : "text-graphite"}`}>{countFor(c)}</span>
            </button>
          ))}
        </div>
        <p className="mt-6 text-[14px] text-graphite" aria-live="polite">Showing {items.length} {items.length === 1 ? "project" : "projects"}</p>

        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <li key={p.url}>
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex h-full flex-col justify-between rounded-md border border-stone bg-white p-6 transition-colors hover:border-charcoal"
                data-testid="portfolio-item"
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-rust-ink">{p.category}</p>
                  <h3 className="mt-3 font-display text-lg font-bold leading-snug">{p.name}</h3>
                  {p.tag && <p className="mt-2 text-[14px] leading-relaxed text-graphite">{p.tag}</p>}
                </div>
                <p className="mt-6 flex items-center justify-between gap-3 border-t border-stone pt-4 text-[13px]">
                  <span className="truncate text-graphite">{hostOf(p.url)}</span>
                  <span className="inline-flex shrink-0 items-center gap-1 font-semibold text-rust-ink group-hover:text-charcoal">
                    {LINK_LABEL[p.category]} <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                  </span>
                </p>
              </a>
            </li>
          ))}
        </ul>
      </section>

      <CtaBand title="Have a project in mind? Let's build it together." />
    </>
  );
}
